export type DomainErrorCode =
    | 'VALIDATION'
    | 'APPROVED_STRUCTURE'
    | 'LOCKED_STRUCTURE'
    | 'STALE_REFERENCE'
    | 'REVIEW_LOCKED'
    | 'APPROVED_REGISTRATION'
    | 'DUPLICATE_EXTERNAL_ROI'
    | 'IMAGE_NOT_HU_QUANTIFIED'
    | 'REFORMAT_UNSUPPORTED'
    | 'IMAGE_NOT_SUV_QUANTIFIED';

export abstract class DomainError extends Error {
    abstract readonly code: DomainErrorCode;

    constructor(message: string) {
        super(message);
        // Restore prototype chain — required for instanceof to work after transpilation.
        Object.setPrototypeOf(this, new.target.prototype);
        this.name = new.target.name;
    }
}

export class DomainValidationError extends DomainError {
    readonly code = 'VALIDATION';
}

export class ApprovedStructureError extends DomainError {
    readonly code = 'APPROVED_STRUCTURE';
}

/**
 * Distinct from {@link ApprovedStructureError}: a lock is self-service,
 * approval is not, and catch sites surface different remedies.
 */
export class LockedStructureError extends DomainError {
    readonly code = 'LOCKED_STRUCTURE';
}

export class StaleReferenceError extends DomainError {
    readonly code = 'STALE_REFERENCE';
}

/**
 * Thrown when a per-ROI review action (approve/reject/unapprove) targets
 * a ROI whose parent structure set is signed off (APPROVED or REJECTED).
 * A signed-off set is locked; the reviewer must unapprove the whole set
 * before re-reviewing individual structures. See the revised design §2.9.
 */
export class ReviewLockedError extends DomainError {
    readonly code = 'REVIEW_LOCKED';
}

/**
 * Thrown when a content edit (rename / setDescription / delete / invert)
 * targets an approved registration. Registration approval is an app-level
 * edit-lock (DICOM does not carry it). See the registration slice spec.
 */
export class ApprovedRegistrationError extends DomainError {
    readonly code = 'APPROVED_REGISTRATION';
}

/**
 * Thrown when a mutation would leave a structure set with more than one
 * EXTERNAL (body contour) ROI — creating a second EXTERNAL structure, or
 * retyping an existing structure to EXTERNAL beside one. RTSTRUCT consumers
 * (TPS dose calculation) treat EXTERNAL as *the* patient outline, so a
 * duplicate is a clinical ambiguity, not a cosmetic one. The invariant is
 * enforced here in the domain — UI checks merely mirror it.
 */
export class DuplicateExternalRoiError extends DomainError {
    readonly code = 'DUPLICATE_EXTERNAL_ROI';
}

/**
 * Thrown when an operation that interprets voxel values as Hounsfield Units
 * (e.g. body-contour extraction) is invoked on an image that is not
 * HU-quantified — wrong modality, an explicit non-HU Rescale Type, or a
 * degenerate intensity range. See {@link Image3D.isHuQuantified}.
 */
export class ImageNotHuQuantifiedError extends DomainError {
    readonly code = 'IMAGE_NOT_HU_QUANTIFIED';
}

/**
 * Thrown when a reformat cannot proceed — see `reformatRegionRefusal` for
 * the conditions. The UI gates on the same predicate, so reaching this
 * error means an entry point bypassed that gate.
 *
 * `reason` is the machine-readable condition, carried so the UI can show
 * the same wording its own gate does. The message is for logs and is not
 * meant to be read by a user: it holds the raw reason code.
 */
export class ReformatUnsupportedError extends DomainError {
    readonly code = 'REFORMAT_UNSUPPORTED';

    constructor(public readonly reason: string) {
        super(`Reformat refused: ${reason}`);
    }
}

/**
 * Thrown when an operation that interprets voxel values as SUVbw-scalable
 * activity (e.g. SUV-threshold segmentation) is invoked on an image that
 * is not SUV-quantified — wrong modality, Units other than BQML, decay
 * correction other than START, an uncomputable SUVbw factor, or a
 * degenerate intensity range. See {@link Image3D.isSuvQuantified}.
 */
export class ImageNotSuvQuantifiedError extends DomainError {
    readonly code = 'IMAGE_NOT_SUV_QUANTIFIED';
}
